// src/common/components/printableOrder/PrintableOrder.tsx
import React from "react";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { OrderInterface } from "../../../common/types/types";
import { OrderItem } from "../../../common/types/types";
import {
  getDeliveryTypeText,
  getOrderStatusText,
  getPaymentMethodText,
} from "../../../common/utils/functions/enumToText";
import styles from "./PrintableOrder.module.scss";

interface PrintableOrderProps {
  order: OrderInterface;
}

export const PrintableOrder: React.FC<PrintableOrderProps> = ({ order }) => {
  const fullAddress = order?.fullAddress;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <img src="/icons/logo512.png" alt="Logo" className={styles.logo} />
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>Заказ #{order.id}</h1>
          {order.createdAt && (
            <p className={styles.date}>
              {format(new Date(order.createdAt), "dd.MM.yyyy HH:mm", {
                locale: ru,
              })}
            </p>
          )}
        </div>
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Контактные данные</h2>
        <p className={styles.text}>
          Телефон: {order.phone_number || "Не указан"}
        </p>
        {order.description && (
          <p className={styles.text}>Комментарий: {order.description}</p>
        )}
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Статус</h2>
        <p className={styles.text}>{getOrderStatusText(order.status)}</p>
      </div>

      {fullAddress && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Адрес доставки</h2>
          <p className={styles.text}>{fullAddress.address?.value}</p>
          <p className={styles.text}>{fullAddress.city?.value}</p>
          {fullAddress.apartment && (
            <p className={styles.text}>кв. {fullAddress.apartment}</p>
          )}
          {fullAddress.entrance && (
            <p className={styles.text}>под. {fullAddress.entrance}</p>
          )}
          {fullAddress.floor && (
            <p className={styles.text}>эт. {fullAddress.floor}</p>
          )}
          {fullAddress.intercom && (
            <p className={styles.text}>домофон {fullAddress.intercom}</p>
          )}
        </div>
      )}

      {order.deliveryType === 1 && order.store && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Самовывоз</h2>
          <p className={styles.text}>{order.store.name}</p>
          <p className={styles.text}>{order.store.address?.value}</p>
          <p className={styles.text}>{order.store.city?.value}</p>
        </div>
      )}

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Оплата</h2>
        <p className={styles.text}>
          {getPaymentMethodText(order.paymentMethod)}
        </p>
        <p className={styles.text}>{order.isPaid ? "Оплачен" : "Не оплачен"}</p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Товары</h2>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Название</th>
              <th className={styles.th}>Кол-во</th>
              <th className={styles.th}>Цена</th>
              <th className={styles.th}>Сумма</th>
            </tr>
          </thead>
          <tbody>
            {order.items?.map((item: OrderItem, index: number) => (
              <tr key={index}>
                <td className={styles.td}>{item.name}</td>
                <td className={styles.td}>{item.quantity}</td>
                <td className={styles.td}>
                  {Number(item.price).toLocaleString("ru-RU")} ₽
                </td>
                <td className={styles.td}>
                  {Number(item.total).toLocaleString("ru-RU")} ₽
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className={styles.summary}>
          <p className={styles.text}>
            Подытог: {Number(order.subtotal).toLocaleString("ru-RU")} ₽
          </p>
          <p className={styles.text}>
            Доставка: {Number(order.deliveryFee).toLocaleString("ru-RU")} ₽
          </p>
          <p className={styles.total}>
            Итого: {Number(order.total).toLocaleString("ru-RU")} ₽
          </p>
        </div>
      </div>

      {order.notes && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Примечания</h2>
          <p className={styles.text}>{order.notes}</p>
        </div>
      )}
    </div>
  );
};
